import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    MenuItem,
    TextField,
} from "@mui/material";
import { GetV2Channel_getV2Channel_ingests, GetV2Channel_getV2Channel_regions } from "../__generated__/GetV2Channel";
import { useEffect, useState } from "react";
import gql from "graphql-tag";
import { useMutation } from "react-apollo";
import { UpdateV2Ingest, UpdateV2IngestVariables } from "./__generated__/UpdateV2Ingest";
import { GET_V2_CHANNEL } from "../V2ChannelDetails";
import { toast } from "react-toastify";
import HESPFormLabel from "../../../../HESPFormLabel/HESPFormLabel";

interface Props {
    open: boolean;
    channelId: string;
    organizationId: string;
    onClose: () => void;
    onConfirm: (name: string, type: string, url: string) => void;
    ingest: Omit<GetV2Channel_getV2Channel_ingests, "__typename">;
    regions: GetV2Channel_getV2Channel_regions[];
}

export default function UpdateV2IngestDetailsDialog({
    ingest,
    onClose,
    onConfirm,
    channelId,
    organizationId,
    regions,
    open,
}: Props) {
    const [name, setName] = useState<string>(ingest.name);
    const [type, setType] = useState<string>(ingest.type);
    const [url, setUrl] = useState<string>(ingest.url);
    const [region, setRegion] = useState<string>(
        ingest.region ? ingest.region : regions.length > 0 ? regions[0].id : "",
    );

    useEffect(() => {
        setName(ingest.name);
        setType(ingest.type);
        setUrl(ingest.url);
        setRegion(ingest.region ? ingest.region : regions.length > 0 ? regions[0].id : "");
    }, [open, ingest, regions]);

    const [updateIngest, { loading }] = useMutation<UpdateV2Ingest, UpdateV2IngestVariables>(UPDATE_V2_INGEST);

    async function onUpdate(): Promise<void> {
        try {
            console.log(type, region);
            if (type === "rtmp-push" && region === "") {
                return;
            }
            await updateIngest({
                variables: {
                    input: {
                        id: ingest.id,
                        organizationId,
                        updates: {
                            name,
                            type,
                            url,
                            ...(type === "rtmp-push" && { region }),
                        },
                    },
                },
                refetchQueries: [{ query: GET_V2_CHANNEL, variables: { organizationId, channelId } }],
            });
            toast.success("Ingest updated");
            onConfirm(name, type, url);
        } catch (e: any) {
            toast.error(e.graphQLErrors[0].message ?? "Unknown error");
        }
    }

    return (
        <Dialog open={open} fullWidth>
            <DialogTitle>Update basic ingest details</DialogTitle>
            <DialogContent>
                <div className="flex flex-col gap-4 w-full">
                    <FormControl fullWidth>
                        <HESPFormLabel label="Name" />
                        <TextField value={name} onChange={(e) => setName(e.target.value)} />
                    </FormControl>
                    <FormControl fullWidth>
                        <HESPFormLabel label="Type" />

                        <div className="flex flex-row gap-3 flex-wrap">
                            <button
                                className={`text-sm rounded-md px-2 py-1 border-[1px] ${
                                    type === "srt-pull" ? "border-yellow-500 bg-yellow-50" : "border-slate-200"
                                }`}
                                onClick={() => {
                                    setType("srt-pull");
                                    setUrl("");
                                }}
                            >
                                SRT pull
                            </button>
                            <button
                                onClick={() => {
                                    setType("rtmp-pull");
                                    setUrl("");
                                }}
                                className={`text-sm rounded-md px-2 py-1 border-[1px] ${
                                    type === "rtmp-pull" ? "border-yellow-500 bg-yellow-50" : "border-slate-200"
                                }`}
                            >
                                RTMP pull
                            </button>
                            <button
                                onClick={() => setType("rtmp-push")}
                                className={`text-sm rounded-md px-2 py-1 border-[1px] ${
                                    type === "rtmp-push" ? "border-yellow-500 bg-yellow-50" : "border-slate-200"
                                }`}
                            >
                                RTMP push
                            </button>
                        </div>
                    </FormControl>
                    {type !== "rtmp-push" && (
                        <FormControl fullWidth>
                            <HESPFormLabel label="URL" />
                            <TextField value={url} onChange={(e) => setUrl(e.target.value)} />
                        </FormControl>
                    )}
                    {type === "rtmp-push" && (
                        <FormControl fullWidth>
                            <HESPFormLabel label="Region" />
                            {regions.length === 0 && <div>No regions that support RTMP push available</div>}
                            {regions.length > 0 && (
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    name="resolution"
                                    onChange={(e) => setRegion(e.target.value)}
                                    variant="outlined"
                                    value={region}
                                >
                                    {regions.map((r, i) => (
                                        <MenuItem value={r.id} key={`r-${i}`} style={{ fontSize: "14px" }}>
                                            {r.id}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        </FormControl>
                    )}
                </div>
            </DialogContent>
            <DialogActions className=" flex flex-row gap-2 justify-end">
                <Button variant="contained" color="inherit" onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button disabled={loading} variant="contained" color="primary" onClick={() => onUpdate()}>
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export const UPDATE_V2_INGEST = gql`
    mutation UpdateV2Ingest($input: UpdateV2IngestInput!) {
        updateV2Ingest(input: $input)
    }
`;
